// Shared UI primitives for the planning surfaces (Work Plan, Roster,
// Labor Standards). Compose these instead of restyling MUI ad hoc so the
// three pages read as one product.
//
// Built to the Wilya Design System (see tokens.ts): hairlines rather than
// shadows, small radii, flat fills, brick used sparingly, Inter for product
// UI, mono for machine data.
import AddIcon from "@mui/icons-material/Add";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React from "react";

import { planner,ROLE_COLORS,roleColor,roleTextColor,staffingColor,staffingLabel,staffingSoft,StaffingState } from "./tokens"; // eslint-disable-line sort-imports

export type { StaffingState } from "./tokens";
export { planner, ROLE_COLORS, roleColor, roleTextColor, staffingColor, staffingLabel, staffingSoft } from "./tokens";

// Full-bleed page shell: warm alloy canvas, product UI face for everything
// inside it (the app theme is Roboto; planning surfaces are Inter).
export function PlannerPage({ children }: { children: React.ReactNode }) {
	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				height: "100%",
				overflow: "auto",
				bgcolor: planner.canvas,
				fontFamily: planner.fontUi,
				px: 3,
				py: 2.5,
			}}
		>
			{children}
		</Box>
	);
}

// Page header: title + context on the left, actions on the right.
export function PlannerHeader({
	title,
	subtitle,
	actions,
}: {
	title: string;
	subtitle?: React.ReactNode;
	actions?: React.ReactNode;
}) {
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "flex-start",
				justifyContent: "space-between",
				gap: 2,
				mb: 2.5,
				flexWrap: "wrap",
			}}
		>
			<Box sx={{ minWidth: 0 }}>
				<Typography sx={{ ...planner.type.pageTitle, color: planner.navy }}>
					{title}
				</Typography>
				{subtitle ? (
					<Typography sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.inkSecondary, mt: 0.5 }}>
						{subtitle}
					</Typography>
				) : null}
			</Box>
			{actions ? (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>{actions}</Box>
			) : null}
		</Box>
	);
}

// Caps eyebrow — wide tracking, brick on light grounds (design system rule 7).
export function SectionLabel({ children, sx }: { children: React.ReactNode; sx?: object }) {
	return (
		<Typography
			component="div"
			sx={{ ...planner.labelCaps, fontFamily: planner.fontUi, color: planner.accent, ...sx }}
		>
			{children}
		</Typography>
	);
}

// The standard surface card. The 1.5px hairline is the structure — no resting
// shadow. Hover darkens the edge rather than lifting the card.
export function PlannerCard({
	children,
	onClick,
	accent,
	sx,
}: {
	children: React.ReactNode;
	onClick?: () => void;
	accent?: StaffingState;
	sx?: object;
}) {
	return (
		<Box
			onClick={onClick}
			sx={{
				bgcolor: planner.surface,
				border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
				borderLeft: accent
					? `3px solid ${staffingColor(accent)}`
					: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
				borderRadius: `${planner.radius}px`,
				boxShadow: planner.shadowRest,
				transition: `border-color ${140}ms ease, box-shadow 140ms ease`,
				...(onClick
					? {
						cursor: "pointer",
						"&:hover": {
							borderColor: planner.navy,
							boxShadow: planner.shadowHover,
						},
					}
					: {}),
				...sx,
			}}
		>
			{children}
		</Box>
	);
}

// Compact stat pill: caps label above, mono tabular figure below.
export function StatPill({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
	const pill = (
		<Box
			sx={{
				px: 1.75,
				py: 1,
				bgcolor: planner.surface,
				border: `${planner.borderWidth}px solid ${planner.hairline}`,
				borderRadius: `${planner.radiusSm}px`,
				minWidth: 92,
			}}
		>
			<SectionLabel>{label}</SectionLabel>
			<Typography
				sx={{
					...planner.type.stat,
					fontFamily: planner.fontMono,
					color: planner.navy,
					mt: 0.25,
				}}
			>
				{value}
			</Typography>
		</Box>
	);
	return hint ? <Tooltip title={hint}>{pill}</Tooltip> : pill;
}

// Status badge — mono caps, tight radius (design system badge recipe).
export function StaffingChip({ state, label }: { state: StaffingState; label?: string }) {
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				px: "10px",
				py: "4px",
				borderRadius: `${planner.radiusXs}px`,
				bgcolor: staffingSoft(state),
				color: staffingColor(state),
				border: `1px solid ${staffingColor(state)}33`,
				fontFamily: planner.fontMono,
				fontSize: 11,
				fontWeight: 500,
				letterSpacing: "0.08em",
				textTransform: "uppercase",
				lineHeight: 1.3,
				whiteSpace: "nowrap",
			}}
		>
			{label ?? staffingLabel(state)}
		</Box>
	);
}

// Thin progress meter with a mono count, e.g. phases staffed 12/35
export function ProgressMeter({
	value,
	total,
	state,
}: {
	value: number;
	total: number;
	state: StaffingState;
}) {
	const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
			<LinearProgress
				variant="determinate"
				value={pct}
				sx={{
					flex: 1,
					height: 4,
					borderRadius: `${planner.radiusXs}px`,
					bgcolor: planner.alloy,
					"& .MuiLinearProgress-bar": {
						bgcolor: staffingColor(state),
						borderRadius: `${planner.radiusXs}px`,
					},
				}}
			/>
			<Typography
				sx={{
					...planner.type.metaStrong,
					fontFamily: planner.fontMono,
					color: planner.inkSecondary,
					fontVariantNumeric: "tabular-nums",
					flexShrink: 0,
				}}
			>
				{value}/{total}
			</Typography>
		</Box>
	);
}

// Machine data: identifiers, VINs, shift codes, times (design system rule 8).
export function MonoText({ children, sx }: { children: React.ReactNode; sx?: object }) {
	return (
		<Typography
			component="span"
			sx={{
				fontFamily: planner.fontMono,
				fontSize: 12,
				color: planner.inkSecondary,
				letterSpacing: 0,
				...sx,
			}}
		>
			{children}
		</Typography>
	);
}

// Empty state with optional primary action
export function EmptyState({
	title,
	body,
	actionLabel,
	onAction,
	icon,
	compact,
}: {
	title: string;
	body?: string;
	actionLabel?: string;
	onAction?: () => void;
	icon?: React.ReactNode;
	compact?: boolean;
}) {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				textAlign: "center",
				py: compact ? 3 : 8,
				px: 2,
				fontFamily: planner.fontUi,
				color: planner.inkMuted,
			}}
		>
			{icon ?? <InboxOutlinedIcon sx={{ fontSize: compact ? 28 : 40, mb: 1, color: planner.inkMuted }} />}
			<Typography sx={{ ...(compact ? planner.type.bodyStrong : planner.type.title), color: planner.inkSecondary }}>
				{title}
			</Typography>
			{body ? (
				<Typography sx={{ ...planner.type.body, mt: 0.5, maxWidth: 420, color: planner.inkMuted }}>
					{body}
				</Typography>
			) : null}
			{actionLabel && onAction ? (
				<GhostButton size="small" startIcon={<AddIcon />} onClick={onAction} sx={{ mt: 1.5 }}>
					{actionLabel}
				</GhostButton>
			) : null}
		</Box>
	);
}

// Primary action — brick. One per viewport (design system rule 4).
export function PrimaryButton(props: React.ComponentProps<typeof Button>) {
	return (
		<Button
			variant="contained"
			disableElevation
			{...props}
			sx={{
				textTransform: "none",
				fontFamily: planner.fontUi,
				fontSize: 14,
				fontWeight: 600,
				borderRadius: `${planner.radiusSm}px`,
				border: `${planner.borderWidth}px solid ${planner.accent}`,
				bgcolor: planner.accent,
				color: planner.paper,
				px: 2.5,
				py: 1.25,
				"&:hover": { bgcolor: planner.accentDeep, borderColor: planner.accentDeep },
				...props.sx,
			}}
		/>
	);
}

// Secondary action — navy outline that fills on hover.
export function GhostButton(props: React.ComponentProps<typeof Button>) {
	return (
		<Button
			variant="outlined"
			{...props}
			sx={{
				textTransform: "none",
				fontFamily: planner.fontUi,
				fontSize: 14,
				fontWeight: 600,
				borderRadius: `${planner.radiusSm}px`,
				color: planner.navy,
				border: `${planner.borderWidth}px solid ${planner.navy}`,
				bgcolor: "transparent",
				px: 2,
				py: 1.25,
				"&:hover": {
					bgcolor: planner.navy,
					borderColor: planner.navy,
					color: planner.paper,
				},
				...props.sx,
			}}
		/>
	);
}
